import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PortalContentService } from '../../services/portal-content.service';
import { BillingService } from '../../services/billing.service';
import { LanguageService } from '../../services/language.service';
import { PortalItemDto, PortalSlideDto } from '../../models/portal';
import { BillingInterval, PlanDto } from '../../models/billing';

interface PlanFeature { label: string; ok: boolean; }

/**
 * Public marketing landing for the apex host. Hero / steps / features / stats /
 * testimonials / pricing / FAQ are all loaded from the admin-managed CMS
 * (/PortalContent/Public) with built-in fallbacks, so it never renders empty.
 * The actual "request a tenant" form lives on a separate wizard page
 * (/portal/become-a-tenant).
 */
@Component({
  selector: 'app-portal-home',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  // CMS-loaded content
  private contentMap: Record<string, { en?: string | null; ar?: string | null }> = {};
  slides: PortalSlideDto[] = [];
  showSlider = false; // temporarily hidden — flip to true to restore the showcase carousel
  activeSlide = 0;
  private slideTimer?: any;
  private loadedSteps: PortalItemDto[] = [];
  private loadedFeatures: PortalItemDto[] = [];
  stats: PortalItemDto[] = [];
  faqs: PortalItemDto[] = [];
  testimonials: PortalItemDto[] = [];
  openFaq = -1;

  // pricing
  plans: PlanDto[] = [];
  readonly currencies = ['EGP', 'SAR', 'USD'];
  selectedCurrency = 'EGP';
  interval: BillingInterval = 1; // Monthly=1, Annual=2

  private observer?: IntersectionObserver;

  /** Fallback content (used when the CMS has nothing) — resolved per language. */
  get steps(): { title: string; text: string }[] {
    return [1, 2, 3].map((n) => ({
      title: this.t(`portal.Step${n}Title`),
      text: this.t(`portal.Step${n}Text`),
    }));
  }

  /** Icon + copy for the capabilities grid (icons are bootstrap-icons classes). */
  private readonly featureIcons = [
    'bi-globe2', 'bi-people', 'bi-journal-richtext',
    'bi-camera-video', 'bi-bar-chart', 'bi-shield-check',
  ];
  get features(): { icon: string; title: string; text: string }[] {
    return this.featureIcons.map((icon, i) => ({
      icon,
      title: this.t(`portal.Feat${i + 1}Title`),
      text: this.t(`portal.Feat${i + 1}Text`),
    }));
  }

  constructor(
    private cms: PortalContentService,
    private billing: BillingService,
    private host: ElementRef<HTMLElement>,
    private title: Title,
    private language: LanguageService,
    private translate: TranslateService,
  ) {}

  /** Shorthand for a translated string (fallback copy + non-template labels). */
  t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  ngOnInit(): void {
    this.title.setTitle('Menassaty — Run your own branded academy');

    this.cms.getPublic().subscribe({
      next: (res) => {
        if (!res.isSuccess || !res.value) return;
        this.contentMap = {};
        (res.value.content ?? []).forEach((c) => (this.contentMap[c.key] = { en: c.valueEn, ar: c.valueAr }));
        this.slides = (res.value.slides ?? []).filter((s) => s.mediaId);
        this.loadedSteps = res.value.steps ?? [];
        this.loadedFeatures = res.value.features ?? [];
        this.stats = (res.value.stats ?? []).filter((s) => this.pick(s.titleEn, s.titleAr));
        this.faqs = (res.value.faqs ?? []).filter((f) => this.pick(f.titleEn, f.titleAr));
        this.testimonials = (res.value.testimonials ?? []).filter((t) => this.pick(t.textEn, t.textAr));
        if (this.slides.length > 1) this.startSlideTimer();
        setTimeout(() => this.observeReveals());
      },
      error: () => {},
    });

    this.billing.publicPlans().subscribe({
      next: (res) => {
        if (!res.isSuccess || !res.value) return;
        this.plans = [...res.value].sort((a, b) => a.sortOrder - b.sortOrder);
        const available = new Set(this.plans.flatMap((p) => p.prices.map((x) => x.currency)));
        if (!available.has(this.selectedCurrency)) {
          this.selectedCurrency = this.currencies.find((c) => available.has(c)) ?? this.selectedCurrency;
        }
        setTimeout(() => this.observeReveals());
      },
      error: () => {},
    });
  }

  // ── localization + content helpers ──────────────────────────────────────────
  // Via the service so CMS copy re-picks the instant the toggle fires.
  private get lang(): string { return this.language.current; }
  private pick(en?: string | null, ar?: string | null): string {
    return (this.lang === 'ar' ? ar || en : en || ar) ?? '';
  }
  c(key: string, fallback: string): string {
    const v = this.contentMap[key];
    const picked = v ? this.pick(v.en, v.ar) : '';
    return picked || fallback;
  }

  /** Main hero headline (full CMS title, or a fallback first line). */
  get heroTitle(): string { return this.c('hero.title', this.t('portal.HeroTitleFallback')); }
  /**
   * Gradient accent line. Shown only when the admin sets an explicit
   * hero.titleAccent, or — with no CMS title at all — as the fallback second line.
   * This avoids a redundant accent when hero.title already is the full headline.
   */
  get heroAccent(): string {
    const explicit = this.c('hero.titleAccent', '');
    if (explicit) return explicit;
    return this.contentMap['hero.title'] ? '' : this.t('portal.HeroAccentFallback');
  }

  get displaySteps(): { title: string; text: string }[] {
    if (this.loadedSteps.length)
      return this.loadedSteps.map((s) => ({ title: this.pick(s.titleEn, s.titleAr), text: this.pick(s.textEn, s.textAr) }));
    return this.steps;
  }

  /** Prefer CMS feature copy when present; fall back to the rich icon list. */
  get displayFeatures(): { icon: string; title: string; text: string }[] {
    if (this.loadedFeatures.length) {
      return this.loadedFeatures.map((f, i) => ({
        icon: this.features[i % this.features.length].icon,
        title: this.pick(f.titleEn, f.titleAr) || this.features[i % this.features.length].title,
        text: this.pick(f.textEn, f.textAr),
      }));
    }
    return this.features;
  }

  // ── slider ────────────────────────────────────────────────────────────────
  slideImg(s: PortalSlideDto): string | null { return this.cms.mediaUrl(s.mediaId); }
  slideCaption(s: PortalSlideDto): string { return this.pick(s.captionEn, s.captionAr); }
  goToSlide(i: number): void { this.activeSlide = (i + this.slides.length) % this.slides.length; this.restartSlideTimer(); }
  nextSlide(): void { this.goToSlide(this.activeSlide + 1); }
  prevSlide(): void { this.goToSlide(this.activeSlide - 1); }
  pauseSlider(): void { if (this.slideTimer) { clearInterval(this.slideTimer); this.slideTimer = undefined; } }
  resumeSlider(): void { if (!this.slideTimer && this.slides.length > 1) this.startSlideTimer(); }
  private startSlideTimer(): void {
    this.slideTimer = setInterval(() => { this.activeSlide = (this.activeSlide + 1) % this.slides.length; }, 4500);
  }
  private restartSlideTimer(): void {
    if (this.slides.length <= 1) return;
    if (this.slideTimer) clearInterval(this.slideTimer);
    this.startSlideTimer();
  }

  // ── stats + FAQ + testimonials ──────────────────────────────────────────────
  statValue(s: PortalItemDto): string { return this.pick(s.titleEn, s.titleAr); }
  statLabel(s: PortalItemDto): string { return this.pick(s.textEn, s.textAr); }
  faqQuestion(f: PortalItemDto): string { return this.pick(f.titleEn, f.titleAr); }
  faqAnswer(f: PortalItemDto): string { return this.pick(f.textEn, f.textAr); }
  toggleFaq(i: number): void { this.openFaq = this.openFaq === i ? -1 : i; }
  tmQuote(t: PortalItemDto): string { return this.pick(t.textEn, t.textAr); }
  tmAuthor(t: PortalItemDto): string { return this.pick(t.titleEn, t.titleAr); }
  tmRole(t: PortalItemDto): string { return t.linkUrl ?? ''; }
  tmInitial(t: PortalItemDto): string { return (this.tmAuthor(t).trim()[0] ?? '•').toUpperCase(); }

  // ── pricing helpers ─────────────────────────────────────────────────────────
  setCurrency(c: string): void { this.selectedCurrency = c; }
  setInterval(i: BillingInterval): void { this.interval = i; }
  priceFor(p: PlanDto): number | null {
    return p.prices.find((x) => x.currency === this.selectedCurrency && x.interval === this.interval)?.amount ?? null;
  }
  isPopular(i: number): boolean { return this.plans.length >= 3 && i === 1; }
  planName(p: PlanDto): string { return this.pick(p.nameEn, p.nameAr); }
  planDescription(p: PlanDto): string { return this.pick(p.descriptionEn, p.descriptionAr); }
  planFeatures(p: PlanDto): PlanFeature[] {
    const val = (k: string) => p.entitlements.find((e) => e.key === k)?.value;
    const out: PlanFeature[] = [];
    // 'Students' / 'Teachers' pick the pluralized noun inside the phrase, so the
    // whole sentence is one key per language rather than a concatenation.
    const limit = (k: string, noun: 'Students' | 'Teachers') => {
      const v = val(k);
      if (v == null) return;
      const n = +v;
      const label = n < 0
        ? this.t(`portal.Unlimited${noun}`)
        : this.t(`portal.UpTo${noun}`, { n });
      out.push({ label, ok: true });
    };
    const flag = (k: string, key: string) => out.push({ label: this.t(key), ok: val(k) === 'true' });
    limit('maxStudents', 'Students');
    limit('maxTeachers', 'Teachers');
    flag('branding', 'portal.FeatCustomBranding');
    flag('liveClassroom', 'portal.FeatLiveClassroom');
    flag('ai', 'portal.FeatAiAssistant');
    return out;
  }

  // ── reveal-on-scroll ─────────────────────────────────────────────────────────
  ngAfterViewInit(): void {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-visible'); this.observer?.unobserve(e.target); }
        }),
        { threshold: 0.12 },
      );
    }
    this.observeReveals();
  }

  private observeReveals(): void {
    const els = this.host.nativeElement.querySelectorAll('.reveal:not(.is-visible)');
    if (this.observer) els.forEach((el) => this.observer!.observe(el));
    else els.forEach((el) => el.classList.add('is-visible'));
  }

  ngOnDestroy(): void {
    if (this.slideTimer) clearInterval(this.slideTimer);
    this.observer?.disconnect();
  }
}
